









































import Vue from 'vue';
import { defineComponent, onMounted, reactive, ref } from '@vue/composition-api';
import { VNode } from 'vue/types/umd';
import { ResponsiveUtils } from '../../utils';
import PortalVue from 'portal-vue';
Vue.use(PortalVue);
/**
 * Component to display content in tabs. Can and should be used with tab.vue.
 * General structure of the component:
 * * in desktop viewports, the order is TabHeader1 - TabHeader2 - TabContent1 - TabContent2
 * * in mobile viewports, the is TabHeader1 - TabContent1 - TabHeader2 - TabContent2
 * This structure is realized via portals --> They are only enabled in mobile viewports.
 * This way, the tabs can be displayed as accordion in mobile viewports.
 */
export default defineComponent({
	setup(_, { slots, emit }) {
		const tabsRef = ref<HTMLDivElement | null>(null);
		const responsiveUtils = new ResponsiveUtils();

		const state = reactive({
			/**
			 * This extra array for holding the tab Nodes instead of using slots.default() is necessary,
			 * because we don't want to access any empty VNodes within slots.default().
			 * (There might be empty ones, when there is whitespace between two tabs, which is mostly the case.)
			 */
			tabs: [] as any[],
			isSmVP: responsiveUtils.isSm(),
		});

		function selectTab(tabNo: number) {
			state.tabs.forEach((tab: any, index: number) => {
				let active = false;
				// accordion behavior for smaller viewports:
				// close the tab, when the active trigger is clicked
				if (state.isSmVP && tab?.componentInstance?.isActive) {
					active = false;
				} else {
					active = index === tabNo;
				}
				tab.componentInstance.setIsActive(active);
			});
		}

		function endAccTransition(el: HTMLElement) {
			if (!state.isSmVP) return;
			el.style.height = '';
		}

		function beforeEnterTransition(el: HTMLElement, tab: any) {
			if (!state.isSmVP) return;
			tab.componentInstance.$nextTick(() => {
				const tabContent = tab.componentInstance.tabContent;
				if (tabContent) {
					tabContent.style.visibility = 'hidden';
					tabContent.style.display = 'block';
				}
				el.style.display = 'block';
			});
		}

		function afterEnterTransition(el: HTMLElement) {
			endAccTransition(el);
		}

		function startAccTransition(el: HTMLElement, tab: any) {
			if (!state.isSmVP) return;

			tab.componentInstance.$nextTick(() => {
				const tabContent = tab.componentInstance.tabContent;
				if (tabContent) {
					tabContent.style.visibility = 'visible';
				}
				el.style.height = el.scrollHeight + 'px';
			});
		}

		/**
		 * Only for large viewports: activate the first tab,
		 * if the window resized to a large screen and there was no tab activated in the small one
		 */
		function selectDefaultTab() {
			const activeTabs = state.tabs.filter((tab: any) => tab.componentInstance.isActive);
			if (!activeTabs.length) {
				// ts-ignore needed, otherwise setIsActive would throw an error
				// eslint-disable-next-line @typescript-eslint/ban-ts-ignore
				// @ts-ignore
				state.tabs[0].componentInstance?.setIsActive(true);
			}
		}

		/**
		 * Only for small viewports: deselect all tabs so that none is activated
		 * (if none is selected per hash!)
		 */
		function deselectAllTabs() {
			state.tabs.forEach((tab: any) => {
				tab.componentInstance.setIsActive(false);
			});
		}

		/**
		 * In small viewports it's possible that no tab is active (because of the accordion-like behavior).
		 * In the desktop view however, there should always be one tab active.
		 * --> When switching to the lg screen, this resize handler evaluates, if there is an active tab.
		 * If there's none, the first one is activated per default.
		 *
		 * Also, there is an event dispatched, when the viewport changes
		 * (e.g. if there are things that need to be taken care of
		 * **before** the portal for the tabs is (de)activated).
		 */
		function resizeHandler() {
			if (state.isSmVP && responsiveUtils.isMdUp()) {
				emit('portal-switch-started', tabsRef.value);
				selectDefaultTab();
				state.isSmVP = false;
			} else if (!state.isSmVP && responsiveUtils.isSm()) {
				emit('portal-switch-started', tabsRef.value);
				// when switching to mobile --> close all tabs
				deselectAllTabs();
				state.isSmVP = true;
			}
		}
		window.addEventListener('resize', resizeHandler);

		/**
		 * Select the tab which corresponds to the hash in the url.
		 */
		function selectTabViaHash() {
			const hash = window.location.hash.substr(1);
			const isClientSideHash = hash.indexOf('/') === 1;
			if (!hash || isClientSideHash) {
				return;
			}
			// find the corresponding tab to the current hash
			state.tabs.forEach((tab: any, index: number) => {
				// select this tab when it is equal to the one in the hash
				const isActive = tab.componentInstance.tabId === hash;
				// if this tab is already active: don't select it twice
				// (closing it in mobile is taken care of by the transition events)
				if (isActive && tab.componentInstance.isActive && state.isSmVP) {
					return;
				}
				isActive && selectTab(index);
			});
		}

		function hashChangeHandler() {
			selectTabViaHash();
		}
		window.addEventListener('hashchange', hashChangeHandler);

		function initTabs() {
			if (slots.default() && slots.default().length) {
				state.tabs = [...slots.default().filter((vnode: VNode) => vnode.tag !== undefined)];
			}
			// if one of the tabs is set to be initially open --> select this one
			const defaultTabs = state.tabs.filter((tab: any) => tab.componentInstance.isInitiallyOpen);
			if (defaultTabs.length) {
				defaultTabs[0].componentInstance.setIsActive(true);
			}
			selectTabViaHash();
			!state.isSmVP && selectDefaultTab();
		}

		onMounted(() => {
			initTabs();
		});

		/**
		 * Accordion behavior in mobile VP: when the tab is already active and now clicked: Close it.
		 * Otherwise (in desktop VP) open it
		 */
		function toggleTab(index: number) {
			const tabInstance = state.tabs[index] as any;
			if (state.isSmVP && tabInstance.componentInstance.isActive) {
				tabInstance.componentInstance.setIsActive(false);
				history.replaceState(null, '', ' ');
			} else {
				selectTab(index);
			}
		}

		function emitPortalSwitch() {
			emit('portal-switch-ended', tabsRef.value);
		}

		return {
			state,
			startAccTransition,
			endAccTransition,
			beforeEnterTransition,
			afterEnterTransition,
			emitPortalSwitch,
			tabsRef,
			toggleTab,
		};
	},
});
